/**
 * ProfessionalDetailRoute — /marketplace/:professional_id detail page.
 *
 * Renders the full professional profile. When the profile is flagged
 * as demo, the disclosure block appears ABOVE any CTA and the CTA
 * switches from "Request Consultation" to "Notify Me When Real
 * Professionals Join".
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  MapPin,
  Briefcase,
  ExternalLink,
  Loader2,
  Mail,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import AppNav from "@/components/app/AppNav";
import DemoBadge from "@/components/marketplace/DemoBadge";
import DemoDisclosure from "@/components/marketplace/DemoDisclosure";
import ConsultationRequestModal from "@/components/marketplace/ConsultationRequestModal";
import NotifyInterestModal from "@/components/marketplace/NotifyInterestModal";

const CATEGORY_LABELS = {
  grant_writing: "Grant Writing",
  budget_planning: "Budget Planning",
  cpa_services: "CPA Services",
  nonprofit_formation: "Nonprofit Formation",
  business_formation: "Business Formation",
  credit_repair: "Credit Repair",
  business_plan: "Business Plan",
  sales_marketing: "Sales & Marketing",
};

function renderServiceArea(sa) {
  if (sa === "nationwide") return "Nationwide";
  if (Array.isArray(sa)) return sa.join(" · ");
  return "—";
}

export default function ProfessionalDetailRoute() {
  const { professionalId } = useParams();
  const navigate = useNavigate();
  const [pro, setPro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consultationOpen, setConsultationOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);

  const load = useCallback(() => {
    if (!professionalId) return;
    setLoading(true);
    setError(null);
    api
      .get(`/marketplace/professionals/${professionalId}`)
      .then((r) => setPro(r.data))
      .catch((err) => {
        if (err?.response?.status === 404) {
          setError("Professional not found. It may have been removed.");
        } else {
          setError(formatApiError(err));
        }
        setPro(null);
      })
      .finally(() => setLoading(false));
  }, [professionalId]);

  useEffect(() => {
    load();
  }, [load]);

  const isDemo = Boolean(pro?.is_demo || pro?.demo_profile);

  return (
    <div
      data-testid="marketplace-detail-root"
      className="min-h-screen"
      style={{ background: "var(--v2-background, #0A0A0F)", color: "var(--v2-text, #F5F5F0)" }}
    >
      <AppNav />
      <main className="mx-auto max-w-4xl px-4 md:px-8 py-8 md:py-12 space-y-6">
        <div>
          <Link
            to="/marketplace"
            data-testid="marketplace-detail-back-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold"
            style={{ color: "var(--v2-accent-strong, #5D50C8)" }}
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Back to marketplace
          </Link>
        </div>

        {loading && (
          <div
            data-testid="marketplace-detail-loading"
            className="animate-pulse rounded-2xl p-6"
            style={{
              background: "var(--v2-surface, #13131A)",
              border: "1px solid var(--v2-border, rgba(255,255,255,0.08))",
            }}
          >
            <div className="h-6 w-2/3 rounded bg-white/10 mb-3" />
            <div className="h-4 w-1/3 rounded bg-white/5 mb-6" />
            <div className="h-3 w-full rounded bg-white/5 mb-2" />
            <div className="h-3 w-11/12 rounded bg-white/5 mb-2" />
            <div className="h-3 w-10/12 rounded bg-white/5" />
          </div>
        )}

        {error && !loading && (
          <div
            data-testid="marketplace-detail-error"
            role="alert"
            className="rounded-2xl p-6"
            style={{
              background: "rgba(239,68,68,0.10)",
              border: "1px solid rgba(239,68,68,0.34)",
              color: "var(--v2-text, #F5F5F0)",
            }}
          >
            <div className="font-semibold mb-1">Couldn't load this professional</div>
            <div className="text-sm">{error}</div>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={load}
                className="px-3 py-1.5 text-xs font-semibold rounded-md border"
                style={{ borderColor: "var(--v2-border, rgba(255,255,255,0.18))" }}
              >
                Retry
              </button>
              <button
                type="button"
                onClick={() => navigate("/marketplace")}
                className="px-3 py-1.5 text-xs font-semibold rounded-md"
                style={{ background: "var(--v2-accent, #5D50C8)", color: "var(--v2-accent-ink, #FFFFFF)" }}
              >
                Back to marketplace
              </button>
            </div>
          </div>
        )}

        {pro && !loading && !error && (
          <>
            {/* Header block */}
            <header
              className="rounded-2xl p-6 md:p-8 space-y-3"
              style={{
                background: "var(--v2-surface, #13131A)",
                border: isDemo
                  ? "1px solid rgba(167,139,250,0.42)"
                  : "1px solid var(--v2-border, rgba(255,255,255,0.08))",
              }}
              aria-describedby={isDemo ? "demo-profile-disclosure" : undefined}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1.5">
                  <h1
                    className="text-[24px] md:text-[30px] font-semibold leading-tight"
                    data-testid="marketplace-detail-name"
                    style={{
                      color: "var(--v2-text, #F5F5F0)",
                      fontFamily: "'Space Grotesk', 'Inter', sans-serif",
                    }}
                  >
                    {pro.name}
                  </h1>
                  <div className="flex flex-wrap gap-1.5">
                    {(pro.categories || []).map((slug) => (
                      <span
                        key={slug}
                        className="text-[11px] uppercase tracking-[0.14em] font-medium rounded-full px-2.5 py-0.5"
                        style={{
                          background: "var(--v2-accent-soft, rgba(167,139,250,0.15))",
                          color: "var(--v2-accent-strong, #5D50C8)",
                        }}
                      >
                        {CATEGORY_LABELS[slug] || slug.replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                </div>
                {isDemo && <DemoBadge />}
              </div>

              <div
                className="flex flex-wrap gap-x-6 gap-y-2 pt-3 text-xs md:text-sm"
                style={{
                  borderTop: "1px solid var(--v2-border, rgba(255,255,255,0.08))",
                  color: "var(--v2-text-muted, #A1A1AA)",
                }}
              >
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                  {renderServiceArea(pro.service_area)}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Briefcase className="h-3.5 w-3.5" aria-hidden="true" />
                  {pro.years_experience} years
                </span>
                <span
                  className="inline-flex items-center gap-1.5 tabular-nums font-semibold"
                  style={{ color: "var(--v2-accent-strong, #5D50C8)" }}
                >
                  {pro.rate_range} · Typical rate range
                </span>
                {pro.website_url && (
                  <a
                    data-testid="marketplace-detail-website"
                    href={pro.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5"
                    style={{ color: "var(--v2-accent-strong, #5D50C8)" }}
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /> Visit website
                  </a>
                )}
              </div>
            </header>

            {/* Demo disclosure MUST appear BEFORE any CTA */}
            {isDemo && <DemoDisclosure />}

            {/* Bio */}
            <section
              className="rounded-2xl p-6 md:p-8 space-y-2"
              style={{
                background: "var(--v2-surface, #13131A)",
                border: "1px solid var(--v2-border, rgba(255,255,255,0.08))",
              }}
            >
              <h2
                className="text-lg font-semibold"
                style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
              >
                About
              </h2>
              <p
                className="text-sm md:text-base leading-relaxed"
                style={{ color: "var(--v2-text-secondary, #EDEDE8)" }}
              >
                {pro.bio}
              </p>
              <div
                className="pt-3 text-xs flex items-center gap-2"
                style={{
                  borderTop: "1px solid var(--v2-border, rgba(255,255,255,0.08))",
                  color: "var(--v2-text-muted, #A1A1AA)",
                }}
              >
                <Mail className="h-3.5 w-3.5" aria-hidden="true" />
                Contact preference:{" "}
                <span className="font-semibold" style={{ color: "var(--v2-text-secondary, #EDEDE8)" }}>
                  {pro.public_contact_preference === "phone_visible"
                    ? "Phone visible"
                    : pro.public_contact_preference === "email_intro"
                    ? "Email intro"
                    : "Platform only"}
                </span>
                {pro.public_contact_preference === "phone_visible" && pro.phone_number && (
                  <span>· {pro.phone_number}</span>
                )}
              </div>
            </section>

            {/* CTA — real vs demo */}
            <section className="flex flex-wrap justify-center md:justify-start gap-3">
              {isDemo ? (
                <button
                  type="button"
                  data-testid="notify-interest-btn"
                  onClick={() => setNotifyOpen(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-full"
                  style={{ background: "var(--v2-accent, #5D50C8)", color: "var(--v2-accent-ink, #FFFFFF)" }}
                >
                  Notify me when real professionals join
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="request-consultation-btn"
                  onClick={() => setConsultationOpen(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-full"
                  style={{ background: "var(--v2-accent, #5D50C8)", color: "var(--v2-accent-ink, #FFFFFF)" }}
                >
                  Request Consultation
                </button>
              )}
            </section>

            {/* Modals */}
            <ConsultationRequestModal
              open={consultationOpen}
              onOpenChange={setConsultationOpen}
              professional={pro}
            />
            <NotifyInterestModal
              open={notifyOpen}
              onOpenChange={setNotifyOpen}
              professional={pro}
            />
          </>
        )}
      </main>
    </div>
  );
}
